/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Shared by SignUp (new account) and SignIn (account created but never
// verified) - both just need "enter the code, verify, land the user session".
const OtpVerification = ({ email, onVerified, onBack, initialError = '' }) => {
  const { verifyOtp, resendOtp } = useAuth();
  const [otp, setOtp] = useState('');
  const [error, setError] = useState(initialError);
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return;
    setSubmitting(true);
    setError('');
    const result = await verifyOtp(email, otp);
    setSubmitting(false);
    if (result.success) {
      onVerified(result.user);
    } else {
      setError(result.error);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    const result = await resendOtp(email);
    if (result.success) {
      setInfo('A new code has been sent.');
      setResendCooldown(60);
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="otp-verification">
      <h2>Verify your email</h2>
      <p>We sent a 6-digit code to <strong>{email}</strong>. Enter it below to continue.</p>
      <p className="otp-hint">It can take a minute or two to arrive. If you don't see it, check your spam or junk folder, then use "Resend code".</p>
      {error && <div className="error-message">{error}</div>}
      {info && !error && <div className="info-message">{info}</div>}
      <form onSubmit={handleVerify} className="auth-form">
        <div className="form-group">
          <label>Verification code:</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            autoFocus
            required
          />
        </div>
        <button type="submit" className="auth-button" disabled={submitting || otp.length !== 6}>
          {submitting ? 'Verifying...' : 'Verify'}
        </button>
      </form>
      <div className="auth-link">
        <button
          type="button"
          className="link-button"
          onClick={handleResend}
          disabled={resendCooldown > 0}
        >
          {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
        </button>
        {onBack && (
          <p>
            <button type="button" className="link-button" onClick={onBack}>
              Back
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

export default OtpVerification;
